/* eslint-disable no-fallthrough */
import { useMemo } from 'react';
import gql from 'graphql-tag';
import { useMutation } from 'react-apollo';
import swal from 'sweetalert2';
import useToast from './useToast';

const replyGql = gql`
  mutation acceptUser(
    $userId: ID!
    $condominiumId: ID!
    $referenceId: ID!
    $status: Statuses!
    $reason: String
    $permissions: PermissionsInput
  ) {
    acceptUser(
      userId: $userId
      condominiumId: $condominiumId
      referenceId: $referenceId
      status: $status
      reason: $reason
      permissions: $permissions
    ) {
      _id
      name
    }
  }
`;

const changePermissionGql = gql`
  mutation changePermission($userId: ID!, $condominiumId: ID!, $referenceId: ID!, $permissions: PermissionsInput!) {
    changeUserPermission(
      userId: $userId
      condominiumId: $condominiumId
      referenceId: $referenceId
      permissions: $permissions
    ) {
      _id
    }
  }
`;

const getPermissionsByValue = value => {
  const permissions = { vote: false, comment: false, view: false };

  switch (value) {
    case 'vote':
      permissions.vote = true;
    case 'comment':
      permissions.comment = true;
    case 'view':
      permissions.view = true;
    default:
  }

  return permissions;
};

export default function useCondominiumDwellerActions({ dweller, onCompleted } = {}) {
  const variables = {
    userId: dweller._id,
    condominiumId: dweller.unity && dweller.unity.data,
    referenceId: dweller.unity && dweller.unity._id,
  };
  const [answerRequestFn] = useMutation(replyGql, { variables, onCompleted });
  const [changePermissionFn] = useMutation(changePermissionGql, {
    variables,
    onCompleted,
  });
  const toast = useToast();

  const permission = useMemo(() => {
    const { permissions } = dweller;
    if (!permissions) return '';

    if (permissions.vote) return 'vote';
    if (permissions.comment) return 'comment';
    return 'view';
  }, [dweller.permissions]);

  const answerRequest = async ({ accepted, permission: newPermission }) => {
    const permissions = getPermissionsByValue(newPermission);
    if (accepted) {
      return answerRequestFn({
        variables: { permissions, status: 'approved' },
      });
    }

    const { value } = await swal.fire({
      customClass: {
        inputClass: 'form-control',
      },
      input: 'textarea',
      inputPlaceholder: 'Justifique o motivo da rejeição',
      title: `Recusar participação de ${dweller.name}`,
    });

    if (value && value.length) {
      return answerRequestFn({
        variables: { permissions, status: 'rejected', reason: value },
      });
    }

    return toast.fire({ icon: 'error', text: 'A justificativa é obrigatória' });
  };

  const changePermission = async ({ permission: newPermission, reason }) => {
    if (newPermission !== permission && newPermission === 'view' && (!reason || !reason.type)) {
      return toast.fire({ icon: 'error', text: 'Justificativa é obrigatória' });
    }
    const permissions = { ...getPermissionsByValue(newPermission), reason };
    return changePermissionFn({ variables: { permissions } });
  };

  return { answerRequest, changePermission };
}
